import React, { useCallback, useMemo, useState, memo, useEffect } from "react";
import useCookie from "react-use-cookie";
import PropTypes from "prop-types";
import Header from "@common/Header/Header";
import logo from "@images/logo-ukr-heart.svg";
import Footer from "@common/Footer/Footer";
import ContactSection from "@common/ContactSection/ContactSection";
import {
    CONTACT_US_PAGE_PATH,
    NOT_FOUND_PAGE_PATH,
    GDPR_PAGE_PATH,
    PRIVACY_POLICY_PAGE_PATH,
    DEDICATED_TEAMS_PAGE_PATH,
    SMALL_SIZE_BUTTON,
    SECONDARY_BUTTON,
    THANK_YOU_PATH,
    OFFICE_PATH,
    CAREERS_PATH,
    BLOG_PAGE_PATH,
    HEADER_OFFSET_Y
} from "@constants";
import classNames from "classnames";
import { ReactSVG } from "react-svg";
import infoIcon from "@images/logo-icon.svg";
import Button from "@common/Button/Button";
import { Link } from "gatsby";
import { Waypoint } from "react-waypoint";
import { Context } from "../../../context";
import "./Layout.scss";

const Layout = ({ children, path }) => {
    const [showLinks, setShowLinks] = useState(false);
    const [userToken, setUserToken] = useCookie("impressit");
    const [headerTitle, setHeaderTitle] = useState();
    const [headerPosition, setHeaderPosition] = useState(HEADER_OFFSET_Y)
    const [openMobileFilters, setOpenMobileFilters] = useState(false);
    const [layoutClassName, setLayoutClassName] = useState("layout");
    const [layoutContainerClassName, setLayoutContainerClassName] =
        useState("layout__container");
    const [isContentLoaded, setIsContentLoaded] = useState(false);

    useEffect(() => {
        setIsContentLoaded(true);
        
        const onScrollHandler = () => {
            if(window.scrollY < 0) {
                setHeaderPosition(HEADER_OFFSET_Y)
            }
            else if(window.scrollY <= HEADER_OFFSET_Y) {
                setHeaderPosition(HEADER_OFFSET_Y - window.scrollY)
            }
            else if(headerPosition !== 0){
                setHeaderPosition(0)
            }
        }

        document.addEventListener("scroll", onScrollHandler)

        return () => {
            document.removeEventListener("scroll", onScrollHandler)
        }
    }, []);

    useEffect(() => {
        setLayoutClassName(
            classNames("layout", {
                "layout--show-menu": showLinks === true,
                "layout--small-retreat": path === OFFICE_PATH,
            })
        );
        setLayoutContainerClassName(
            classNames("layout__container", {
                "layout-container--small-retreat": path === OFFICE_PATH,
                "layout--blog": path === BLOG_PAGE_PATH,
                "layout--home-page": path === "/"
            })
        );
    }, [showLinks, path]);

    const layoutBottomContainer = classNames("layout__bottom-container", {
        "layout__bottom-container-open-filters": !!openMobileFilters,
    });

    const handleClick = useCallback(
        e => {
            e.preventDefault();
            setShowLinks(!showLinks);
        },
        [setShowLinks, showLinks]
    );

    const showContactSection = useMemo(
        () =>
            path !== NOT_FOUND_PAGE_PATH &&
            path !== CONTACT_US_PAGE_PATH &&
            path !== PRIVACY_POLICY_PAGE_PATH &&
            path !== GDPR_PAGE_PATH &&
            path !== THANK_YOU_PATH &&
            path?.includes(BLOG_PAGE_PATH) &&
            !path?.includes(CAREERS_PATH) &&
            !/\d/.test(path),
        [
            path,
            NOT_FOUND_PAGE_PATH,
            CONTACT_US_PAGE_PATH,
            PRIVACY_POLICY_PAGE_PATH,
            GDPR_PAGE_PATH,
            THANK_YOU_PATH,
            CAREERS_PATH,
        ]
    );

    const isDedicatedTeamsPage = useMemo(
        () => path === DEDICATED_TEAMS_PAGE_PATH,
        [path, DEDICATED_TEAMS_PAGE_PATH]
    );

    const isHomePage = path === "/";

    return (
        isContentLoaded && (
            <div className={layoutClassName}>
                <div className="layout__support-banner">
                    <a
                        href="https://impressit.info"
                        target="_blank"
                        rel="noreferrer"
                    >
                        <p>
                            <span>Support Ukraine </span>
                            in its Fight for Peace in Europe
                        </p>
                        <ReactSVG src={logo} />
                    </a>
                </div>
                <Header
                    headerTitle={headerTitle}
                    handleClick={handleClick}
                    openMobileFilters={openMobileFilters}
                    setOpenMobileFilters={setOpenMobileFilters}
                    path={path}
                    showLinks={showLinks}
                    headerPosition={headerPosition}
                />
                <main className={layoutContainerClassName}>
                    <Context.Provider
                        value={{
                            setHeaderTitle,
                            openMobileFilters,
                            setOpenMobileFilters,
                        }}
                    >
                        {children}
                    </Context.Provider>
                </main>
                <div className={layoutBottomContainer}>
                    <div className="layout__bottom">
                        {showContactSection ? (
                            <div>
                                <ContactSection
                                    buttonText={
                                        isDedicatedTeamsPage
                                            ? "Get in touch"
                                            : "Contact us page"
                                    }
                                />
                                {isHomePage && (
                                    <Waypoint
                                        onEnter={() =>
                                            setHeaderTitle(
                                                <p>
                                                    Contact us and get
                                                    <strong> impressed</strong>
                                                </p>
                                            )
                                        }
                                    />
                                )}
                            </div>
                        ) : null}
                        <div className="layout__divider" />
                        <Footer />
                    </div>
                </div>
                {!userToken && (
                    <div className="cookie-popup">
                        <ReactSVG
                            src={infoIcon}
                            alt="info-icon"
                            className="cookie-popup__icon"
                        />
                        <p className="cookie-popup__text">
                            Our website uses cookies to ensure you receive the
                            best experience possible.{" "}
                            <Link to="/gdpr" onClick={() => setUserToken(true)}>
                                More info
                            </Link>
                        </p>
                        <Button
                            text="Got it!"
                            type={SECONDARY_BUTTON}
                            size={SMALL_SIZE_BUTTON}
                            handleClick={() => setUserToken(true)}
                        />
                    </div>
                )}
            </div>
        )
    );
};
Layout.propTypes = {
    children: PropTypes.node.isRequired,
};

export default memo(Layout);
